import { useEffect, useMemo, useState } from 'react';
import {
  Circle, LogIn, LogOut, Download, Clock, Users, Wifi, Ticket, ListChecks, Timer,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { API_BASE } from '../../utils/api-base';
import { getWebSocketClient } from '../../utils/websocket-client';

interface TeamActivityProps {
  clientId: string;
  token: string;
}

interface UserRow {
  id: string; name: string; email: string; role: string; isActive: boolean; online: boolean; lastSeen: string | null;
}
interface ActivityRow {
  actorId: string; actorEmail: string; actorName?: string; actorRole: string;
  eventType: string; description: string; metadata: any; createdAt: string;
}
interface Stats {
  totalMembers: number; onlineNow: number; activeToday: number; downloadsToday: number; ticketsToday: number; avgResponseHours: number | null;
}

// Validated categorical palette slots — one hue per series, fixed order (never cycled).
const COLOR_LOGINS = '#2a78d6';
const COLOR_DATA_ACCESS = '#eb6834';
const COLOR_TICKETS = '#e87ba4';

const EVENT_ICON: Record<string, any> = {
  login: LogIn, logout_success: LogOut, logout: LogOut, session_ended: LogOut,
  download_energy_data: Download, download_manual_logs: Download, download_scrap_data: Download, download_historical_data: Download,
  ticket_created: Ticket, ticket_status: Ticket, ticket_updated: Ticket, ticket_comment_added: Ticket, ticket_deleted: Ticket,
};

const EVENT_LABEL: Record<string, string> = {
  login: 'Logged in', logout_success: 'Logged out', logout: 'Logged out', session_ended: 'Went offline',
  download_energy_data: 'Downloaded energy report', download_manual_logs: 'Downloaded manual logs',
  download_scrap_data: 'Downloaded scrap report', download_historical_data: 'Downloaded historical data',
  ticket_created: 'Raised a ticket', ticket_status: 'Updated ticket status', ticket_updated: 'Updated a ticket',
  ticket_comment_added: 'Commented on a ticket', ticket_deleted: 'Deleted a ticket',
  maintenance_window_created: 'Scheduled maintenance', maintenance_window_updated: 'Updated maintenance schedule',
  maintenance_window_cancelled: 'Cancelled maintenance',
};

const CATEGORY_OF: Record<string, 'logins' | 'data' | 'tickets' | 'system'> = {
  login: 'logins', logout_success: 'logins', logout: 'logins', session_ended: 'logins',
  download_energy_data: 'data', download_manual_logs: 'data', download_scrap_data: 'data', download_historical_data: 'data',
  ticket_created: 'tickets', ticket_status: 'tickets', ticket_updated: 'tickets', ticket_comment_added: 'tickets', ticket_deleted: 'tickets',
};

const relativeTime = (iso: string | null) => {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export function TeamActivity({ clientId, token }: TeamActivityProps) {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [hourlyOverview, setHourlyOverview] = useState<any[]>([]);
  const [topActiveUsers, setTopActiveUsers] = useState<{ name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [activityFilter, setActivityFilter] = useState<'all' | 'logins' | 'data' | 'tickets' | 'system'>('all');
  const [, forceTick] = useState(0);

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/user-activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await res.json();
      if (res.ok && payload?.success) {
        setUsers(payload.users || []);
        setActivity(payload.activity || []);
        setStats(payload.stats || null);
        setHourlyOverview((payload.hourlyOverview || []).map((h: any) => ({ ...h, label: `${String(h.hour).padStart(2, '0')}:00` })));
        setTopActiveUsers(payload.topActiveUsers || []);
      }
    } catch (_) { /* keep last known state */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!clientId || !token) return;
    load();

    const ws = getWebSocketClient(token, { clientId });
    const unsubscribe = ws.on('presence_update', (data: any) => {
      setUsers((prev) => prev.map((u) => (u.id === data.userId ? { ...u, online: data.online, lastSeen: data.lastSeen } : u)));
    });

    const tickInterval = setInterval(() => forceTick((n) => n + 1), 30000);
    const refreshInterval = setInterval(load, 60000);

    return () => {
      unsubscribe();
      clearInterval(tickInterval);
      clearInterval(refreshInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name)),
    [users]
  );

  const filteredActivity = useMemo(() => (
    activityFilter === 'all' ? activity : activity.filter((a) => (CATEGORY_OF[a.eventType] || 'system') === activityFilter)
  ), [activity, activityFilter]);

  const statTiles = stats ? [
    { label: 'Total Members', value: stats.totalMembers, icon: Users, sub: 'All roles' },
    { label: 'Online Now', value: stats.onlineNow, icon: Wifi, sub: 'Currently active', color: '#0ca30c' },
    { label: 'Active Today', value: stats.activeToday, icon: Clock, sub: 'Logged in today' },
    { label: 'Downloads Today', value: stats.downloadsToday, icon: Download, sub: 'Reports & data' },
    { label: 'Tickets Raised', value: stats.ticketsToday, icon: Ticket, sub: 'Today' },
    { label: 'Avg Response', value: stats.avgResponseHours != null ? `${stats.avgResponseHours.toFixed(1)}h` : '—', icon: Timer, sub: 'Ticket resolution (30d)' },
  ] : [];

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading team activity...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Team Activity</h2>
        <p className="text-sm text-muted-foreground">Track your team's online status and recent activity in real time.</p>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        {statTiles.map((t) => (
          <div key={t.label} className="border rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] font-medium mb-1 uppercase tracking-wide">
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </div>
            <p className="text-xl font-semibold" style={t.color ? { color: t.color } : undefined}>{t.value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{t.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sortedUsers.map((u) => (
          <div key={u.id} className="border rounded-lg p-4 flex items-center gap-3">
            <Circle className={u.online ? 'h-3 w-3 fill-green-500 text-green-500' : 'h-3 w-3 fill-gray-400 text-gray-400'} />
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{u.name}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email} &bull; {u.role}</p>
            </div>
            <div className="text-xs text-muted-foreground text-right shrink-0">
              {u.online ? <span className="text-green-600 dark:text-green-500 font-medium">Online</span> : (
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {relativeTime(u.lastSeen)}</span>
              )}
            </div>
          </div>
        ))}
        {sortedUsers.length === 0 && (
          <p className="text-sm text-muted-foreground col-span-full">No users found for this client.</p>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Recent Activity</h3>
            <Tabs value={activityFilter} onValueChange={(v: any) => setActivityFilter(v)}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="logins">Logins</TabsTrigger>
                <TabsTrigger value="data">Data Access</TabsTrigger>
                <TabsTrigger value="tickets">Tickets</TabsTrigger>
                <TabsTrigger value="system">System</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="border rounded-lg divide-y max-h-[480px] overflow-y-auto">
            {filteredActivity.length === 0 && (
              <p className="text-sm text-muted-foreground p-4">No activity recorded yet.</p>
            )}
            {filteredActivity.map((a, idx) => {
              const Icon = EVENT_ICON[a.eventType] || ListChecks;
              return (
                <div key={idx} className="flex items-center gap-3 p-3">
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">
                      <span className="font-medium">{a.actorName || a.actorEmail}</span> — {EVENT_LABEL[a.eventType] || a.eventType}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{relativeTime(a.createdAt)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-6">
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3">Team Activity Overview (Today)</h3>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hourlyOverview}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" opacity={0.5} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} interval={3} />
                  <YAxis tick={{ fontSize: 10, fill: '#898781' }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                  <Tooltip />
                  <Legend formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} iconType="line" />
                  <Line type="monotone" dataKey="logins" name="Logins" stroke={COLOR_LOGINS} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="dataAccess" name="Data Access" stroke={COLOR_DATA_ACCESS} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="tickets" name="Tickets" stroke={COLOR_TICKETS} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3">Top Active Users (Today)</h3>
            <div className="space-y-3">
              {topActiveUsers.length === 0 && <p className="text-xs text-muted-foreground">No activity recorded today yet.</p>}
              {topActiveUsers.map((u, i) => {
                const max = topActiveUsers[0]?.count || 1;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium truncate">{u.name}</span>
                      <span className="text-muted-foreground shrink-0 ml-2">{u.count} actions</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(u.count / max) * 100}%`, backgroundColor: COLOR_LOGINS }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
